import type { IPeerConnection } from './types';
import { CONGESTION_MESSAGE_TYPE } from './webrtcLatencyContracts';
import {
  CLIENT_STREAM_STATS_INTERVAL_MS,
  CLIENT_STREAM_STATS_MESSAGE_TYPE,
  type ClientStreamStatsPayload,
} from './clientStreamStatsContracts';
import { GuestStreamStatsSampler, type GuestStatsSample } from './guestStreamStatsSampler';
import { GuestStreamHealthRecorder } from './guestStreamHealthRecorder';
import type { GuestBrowserCapability } from './guestBrowserCapability';
import type { BeaconE2eCongestionLevel } from './beaconWebrtcE2eContracts';
import type { DecodeHintCollector } from './decodeHintCollector';
import { GUEST_DEBUG_MESSAGE_TYPE, type GuestDebugKind } from './guestDebugContracts';
import { isDebugWebrtcLoggingEnabled } from './debugLoggingFlag';
import { DecodeLagDetector } from './decodeLagDetector';
import type { LatencyProbeReport } from './latencyProbeContracts';
import type { InputPhotonSummary } from './inputPhotonContracts';
import type { FramePipelineSummary } from './framePipeline/framePipelineContracts';
import type { EncodedFrameSummary } from './framePipeline/encodedFrameSizeTracker';
import type { ReceiverHintReadback } from './receiverHints/receiverHintContracts';
import { ReceiverCounterTracker } from './streamQuality/receiverCounterTracker';
import type { PresentationSummary } from './streamQuality/streamQualityContracts';
import type { GuestStreamRecoveryBinding } from './integration/GuestStreamRecoveryBinding';
import { FRAME_EVIDENCE_STALE_MS } from './streamRecovery/policy';
import { GuestSignalProjector } from './telemetry/GuestSignalProjector';

/** Produces the per-tick rVFC pipeline summary; drained only on a tick that sends. */
export type FramePipelineSummarySource = { summarizeAndReset(): FramePipelineSummary | undefined };
export type PresentationSummarySource = { summarizeAndReset(): PresentationSummary | undefined };
export type EncodedFrameSummarySource = { summarizeAndReset(): EncodedFrameSummary | undefined; status?(): string };
/** Reports what the video receiver says its low-latency hints are. */
export type ReceiverHintReadbackSource = {
  readback(): ReceiverHintReadback | null;
  /** Game-audio receiver hint, when the controller has pinned one. */
  audioReadback?(): ReceiverHintReadback | null;
};

/**
 * Polls inbound WebRTC stats and reports them to the host.
 *
 * This class holds NO congestion thresholds. It used to: it classified its own link against
 * absolute RTT (elevated at 100ms, critical at 500ms) while the host classified the same link
 * against queueing above a per-peer RTT floor. Two ladders cannot agree, and the absolute one
 * labelled every cellular guest permanently congested — which then held the host's recovery
 * episode open for the life of the session.
 *
 * The guest now measures and the host decides. The only numbers here are a send cadence and
 * a rate limit.
 */

/** How often stats are sampled. Sends are separately rate-limited to the wire interval. */
const POLL_MS = 1_000;

export type CongestionMonitorCallbacks = {
  onFramesDecoded?: (framesDecoded: number) => void;
  /**
   * Fired after every successful sample, before the wire send. The app layer reads this for
   * telemetry so it never has to run a second getStats() poll on a mobile radio.
   */
  onSample?: (sample: GuestStatsSample) => void;
};

export class GuestCongestionMonitor {
  private pollTimer: ReturnType<typeof setInterval> | null = null;
  private channelOpen = false;
  private closed = false;
  private lastStatsSentAt = 0;
  /** Skip overlapping 1 Hz ticks while a prior getStats() is still in flight. */
  private getStatsInFlight = false;
  private activeConnection: IPeerConnection | null = null;
  private pollGeneration = 0;
  private sampleRequested = false;
  private recoveryBinding: GuestStreamRecoveryBinding | null = null;
  /** The host peer id, set once the session knows it; labels the streamHealth wire message. */
  private peerId: string | undefined;
  private readonly sampler: GuestStreamStatsSampler;
  /** Unified per-session health recorder; fed from the same sample, never a second poll. */
  private readonly healthRecorder = new GuestStreamHealthRecorder();
  private lastSample: GuestStatsSample | undefined;
  private lastStatsSent: { payload: ClientStreamStatsPayload; atMs: number } | undefined;
  private lastCongestionSent: { level: BeaconE2eCongestionLevel; atMs: number } | undefined;
  /** Dwell-based decode-lag detector; produces the episode that rides the clientStats stream. */
  private readonly decodeLagDetector = new DecodeLagDetector();
  /** Raw inbound-rtp counters + key inventory, read from the same report (no second poll). */
  private readonly receiverCounters = new ReceiverCounterTracker();
  /** Last drained presentation fps; the decode-lag detector's Safari-path evidence. */
  private lastPresentedFps: number | undefined;
  private lastPresentationSummaryAtMs: number | undefined;
  /** Per-connection clientStats send counter; stamped onto every delivered send. */
  private statsSeq = 0;
  /** Last episode the detector produced (attached to the wire when active). */
  private lastDecodeLagEpisode: import('./decodeLagContracts').DecodeLagEpisode | null = null;

  /** This guest's engine, sent on every clientStats so host policy keys on the real guest. */
  private readonly guestEngine: GuestBrowserCapability['engine'] | undefined;
  private readonly signalProjector: GuestSignalProjector;

  constructor(
    private readonly callbacks: CongestionMonitorCallbacks = {},
    options: { isMobile?: boolean; capability?: GuestBrowserCapability } = {},
  ) {
    this.guestEngine = options.capability?.engine;
    this.signalProjector = new GuestSignalProjector(options.capability?.exposesFreezeCount);
    this.sampler = new GuestStreamStatsSampler({
      isMobile: options.isMobile,
      capability: options.capability,
    });
  }

  setChannelOpen(open: boolean): void {
    this.channelOpen = open;
  }

  /** Label the streamHealth wire message with the host peer id (set once per connection). */
  setPeerId(peerId: string): void {
    this.peerId = peerId;
  }

  /** Permanently stop this monitor. Called when the underlying connection is destroyed. */
  markClosed(): void {
    this.closed = true;
    this.stop();
    this.channelOpen = false;
  }

  resetForNewConnection(): void {
    this.pollGeneration += 1;
    this.sampleRequested = false;
    this.recoveryBinding?.resetForConnection();
    this.closed = false;
    this.lastSample = undefined;
    this.lastStatsSent = undefined;
    this.lastCongestionSent = undefined;
    this.lastStatsSentAt = 0;
    this.getStatsInFlight = false;
    this.lastDecodeLagEpisode = null;
    this.probeSeq = 0;
    this.statsSeq = 0;
    this.lastPresentedFps = undefined;
    this.lastPresentationSummaryAtMs = undefined;
    this.sampler.reset();
    this.healthRecorder.reset();
    this.decodeLagDetector.reset();
    this.receiverCounters.reset();
  }

  /** The unified per-session health recorder (lifecycle events are recorded via the session). */
  getHealthRecorder(): GuestStreamHealthRecorder {
    return this.healthRecorder;
  }

  start(connection: IPeerConnection): void {
    this.stop();
    this.activeConnection = connection;
    this.recoveryBinding?.attach();
    this.sampler.reset();
    this.pollTimer = setInterval(() => {
      void this.poll(connection);
    }, POLL_MS);
  }

  stop(): void {
    this.pollGeneration += 1;
    this.getStatsInFlight = false;
    this.activeConnection = null;
    this.sampleRequested = false;
    this.recoveryBinding?.dispose();
    if (this.pollTimer) {
      clearInterval(this.pollTimer);
      this.pollTimer = null;
    }
  }

  setRecoveryBinding(binding: GuestStreamRecoveryBinding): void { this.recoveryBinding = binding; }

  /** Coalesce event hints with the single in-flight getStats owner. */
  requestSample(): void {
    if (!this.activeConnection || this.closed || !this.channelOpen) return;
    if (this.getStatsInFlight) { this.sampleRequested = true; return; }
    void this.poll(this.activeConnection);
  }

  /** Undefined until a poll actually sampled a succeeded candidate pair. */
  getLastRttMs(): number | undefined {
    return this.lastSample?.wire.rttMs;
  }

  /** Undefined until a poll actually sampled loss over an interval. */
  getLastPacketsLostPct(): number | undefined {
    return this.lastSample?.congestion.packetsLostPct;
  }

  /** Most recent full sample, or undefined before the first successful poll. */
  getLastSample(): GuestStatsSample | undefined {
    return this.lastSample;
  }

  /** Last clientStats payload actually written to the wire. */
  getLastStatsSent(): { payload: ClientStreamStatsPayload; atMs: number } | undefined {
    return this.lastStatsSent;
  }

  getLastCongestionSent(): { level: BeaconE2eCongestionLevel; atMs: number } | undefined {
    return this.lastCongestionSent;
  }

  /**
   * E2E only — forces a legacy `congestion` message so the beacon harness can drive the host
   * without shaping a real network. The production path never emits this message.
   */
  injectCongestion(connection: IPeerConnection, level: BeaconE2eCongestionLevel): void {
    if (!this.channelOpen || this.closed) return;
    const payload = {
      type: CONGESTION_MESSAGE_TYPE,
      level,
      rttMs: Math.round(this.getLastRttMs() ?? 0),
      jbMs: Math.round(this.lastSample?.wire.jbDelayMs ?? 0),
    };
    if (connection.send(JSON.stringify(payload))) {
      this.lastCongestionSent = { level, atMs: Date.now() };
    }
  }

  /** Optional decode-hint producer; drained into the wire payload each send interval. */
  private decodeHintCollector: DecodeHintCollector | null = null;
  /**
   * True once the host's latencyProbe handshake has been answered. While true, the monitor
   * sends calibrated latency reports instead of rVFC hints: the hint path is the fallback,
   * and the calibrated path replaces it the moment the host can decode it.
   */
  private latencyProbeHandshakeLive = false;

  /** Attach the collector that feeds frameDecodeHints onto the clientStats wire. */
  setDecodeHintCollector(collector: DecodeHintCollector | null): void {
    this.decodeHintCollector = collector;
  }

  /** Flip the wire path from rVFC hints to calibrated latency reports. */
  setLatencyProbeHandshakeLive(live: boolean): void {
    this.latencyProbeHandshakeLive = live;
  }

  /**
   * Supplies the rolling input-to-photon summary for the host-facing debug payload.
   *
   * Injected rather than owned: the tracker's lifecycle belongs to the session, and this
   * monitor's job is reporting, not correlation.
   */
  private inputPhotonSummary: (() => InputPhotonSummary) | null = null;

  setInputPhotonSummaryProvider(provider: (() => InputPhotonSummary) | null): void {
    this.inputPhotonSummary = provider;
  }

  /**
   * Standing-latency diagnostics that ride `clientStats`: the per-frame pipeline
   * decomposition and the receiver-hint read-back. Injected, not owned — the session runs the
   * rVFC loop and the hint controller; this monitor only reports what they measured.
   */
  private framePipelineSource: FramePipelineSummarySource | null = null;
  private presentationSource: PresentationSummarySource | null = null;
  private receiverHintSource: ReceiverHintReadbackSource | null = null;
  private encodedFrameSource: EncodedFrameSummarySource | null = null;

  setFramePipelineSampler(source: FramePipelineSummarySource | null): void {
    this.framePipelineSource = source;
  }

  /** What reached the glass per tick (rVFC presentations + element dropped-frame counters). */
  setPresentationSource(source: PresentationSummarySource | null): void {
    this.presentationSource = source;
  }

  /** The receive-side encoded-frame tap (sizes per tick), when the app provides one. */
  setEncodedFrameSource(source: EncodedFrameSummarySource | null): void {
    this.encodedFrameSource = source;
  }

  setReceiverHintSource(source: ReceiverHintReadbackSource | null): void {
    this.receiverHintSource = source;
  }

  private async poll(connection: IPeerConnection): Promise<void> {
    if (!this.channelOpen || this.closed) return;
    if (this.getStatsInFlight) return;
    const rtcPc = connection.getRtcPeerConnection();
    if (!rtcPc || typeof rtcPc.getStats !== 'function') return;

    this.getStatsInFlight = true;
    const generation = this.pollGeneration;
    try {
      const report = await rtcPc.getStats();
      // The channel can close while getStats() is in flight.
      if (!this.channelOpen || this.closed || generation !== this.pollGeneration) return;

      if (this.recoveryBinding?.observeReport(report)) {
        this.sampler.resetForPath(); this.receiverCounters.reset(); this.decodeLagDetector.reset();
        this.lastPresentedFps = undefined; this.lastDecodeLagEpisode = null;
        this.lastPresentationSummaryAtMs = undefined;
      }

      this.receiverCounters.observe(report);
      const sample = this.sampler.sample(report);
      if (!sample) return;
      this.recoveryBinding?.observeSample(sample);
      if (this.recoveryBinding) sample.wire.recoveryEvidence = this.recoveryBinding.evidence();

      this.lastSample = sample;
      // Feed the unified health recorder from the SAME sample (no second getStats poll).
      this.healthRecorder.observeSample(sample);
      if (sample.framesDecoded > 0) {
        this.callbacks.onFramesDecoded?.(sample.framesDecoded);
      }
      this.callbacks.onSample?.(sample);

      // Feed the dwell-based decode-lag detector: it turns the sampler's per-tick backlog +
      // playout trend + decode fps into a single episode, attached to the clientStats wire
      // when active. Safari's transient blips (single-tick dips) are gated by the dwell;
      // Android's sustained backlog activates after DECODE_LAG_DWELL_MS.
      // The presentation summary arrives from the send path (the tracker drains there, so a
      // rate-limited tick's frames roll forward) one tick behind the sample — a 1s skew on a
      // 2s-dwell detector is immaterial and keeps the drain single-owner.
      const playback = sample.wire.recoveryEvidence?.presentation;
      const presentationEligible = !sample.wire.recoveryEvidence ||
        (playback?.visibility === 'visible' && playback.playback === 'playing');
      const presentationAgeMs = this.lastPresentationSummaryAtMs === undefined ? 0 : Date.now() - this.lastPresentationSummaryAtMs;
      // iOS can suspend every hidden poll; a stale summary or clock reversal also breaks dwell.
      if (!presentationEligible || presentationAgeMs < 0 || presentationAgeMs >= FRAME_EVIDENCE_STALE_MS) {
        this.decodeLagDetector.reset(); this.lastPresentedFps = undefined;
      }
      this.lastDecodeLagEpisode = presentationEligible ? this.decodeLagDetector.observe({
        backlogFrames: sample.wire.decodeBacklogFrames ?? 0,
        playoutLatencyTrendMs: sample.wire.playoutLatencyTrendMs ?? 0,
        decodeFps: sample.wire.decodeFps ?? 0,
        standingBacklogFrames: sample.standingBacklogFrames,
        // Residency rides along so the host can tell a decoder that cannot keep up from one
        // that simply was not sent much — the two are indistinguishable by frame rate alone.
        decodeMsPerFrame: sample.wire.decodeMsPerFrame,
        // rVFC presentations: on engines with no frame counters (Safari) a sustained
        // presented-vs-decoded gap is the only decode-side evidence there is.
        presentedFps: this.lastPresentedFps,
      }) : null;

      this.maybeSendStats(connection, sample, presentationEligible);
    } catch {
      // Stats failures are transient; the next poll re-reads.
    } finally {
      if (generation === this.pollGeneration) {
        this.getStatsInFlight = false;
        if (this.sampleRequested) { this.sampleRequested = false; this.requestSample(); }
      }
    }
  }

  /**
   * Forward a diagnostic to the HOST over the data channel (DEBUG BUILD ONLY).
   *
   * The host logs these with a `[GuestDebug.host]` prefix, so a phone that cannot be
   * tethered still leaves a full trace on the host console. Rate-limited implicitly: this
   * is only called from inside the clientStats send path (2s cadence) — never more often
   * than that, and never at all unless debug logging is on.
   */
  private sendDebugToHost(connection: IPeerConnection, kind: GuestDebugKind, detail: Record<string, unknown>): void {
    connection.send(
      JSON.stringify({
        type: GUEST_DEBUG_MESSAGE_TYPE,
        kind,
        detail,
      }),
    );
  }

  private maybeSendStats(connection: IPeerConnection, sample: GuestStatsSample, presentationEligible: boolean): void {
    const now = Date.now();
    if (now - this.lastStatsSentAt < CLIENT_STREAM_STATS_INTERVAL_MS) {
      // DEBUG BUILD ONLY — a stats message dropped by the wire rate limiter is otherwise
      // INVISIBLE: the host keeps seeing the PREVIOUS stats for another full second. On a
      // busy mobile radio the poll fires every 1s but gets sampled into a send gap, which
      // reads on the host as a stale-but-healthy-link — the Android "nothing adapts"
      // shape. Log the skip so a replay can tell "guest sampled nothing" from "sampler is
      // running but the wire is rate-limited". Live check so a test can flip the flag.
      if (isDebugWebrtcLoggingEnabled()) {
        console.log('[GuestStats.skipped]', {
          sinceLastSentMs: now - this.lastStatsSentAt,
          intervalMs: CLIENT_STREAM_STATS_INTERVAL_MS,
          rttMs: sample.wire.rttMs ?? 'absent',
          decodeFps: sample.wire.decodeFps ?? 'absent',
        });
      }
      return;
    }

    // The pipeline summary is drained here, past the rate limiter, so a skipped tick's frames
    // roll into the next send instead of being thrown away. Both fields are omitted, never
    // zero-filled, when nothing was measured or no receiver is attached yet.
    const framePipeline = this.framePipelineSource?.summarizeAndReset();
    const encodedFrames = this.encodedFrameSource?.summarizeAndReset();
    const receiverHint = this.receiverHintSource?.readback() ?? undefined;
    const audioReceiverHint = this.receiverHintSource?.audioReadback?.() ?? undefined;
    const presentation = this.presentationSource?.summarizeAndReset();
    // Stashed for the decode-lag detector's next poll: on engines without frame counters
    // (Safari) the presented-vs-decoded gap is its only episode evidence.
    this.lastPresentedFps = presentationEligible ? presentation?.presentedFps : undefined;
    this.lastPresentationSummaryAtMs = presentationEligible && presentation ? now : undefined;
    const receiverCounters = this.signalProjector.projectReceiverCounters(this.receiverCounters.summarizeAndReset());
    const freezeDelta = this.signalProjector.projectFreezeDelta(receiverCounters);
    const statKeys = this.receiverCounters.pendingStatKeys();
    const payload: ClientStreamStatsPayload = {
      // Stamped on the guest so the sample survives a host that cannot log it promptly; see
      // sampledAtEpochMs in clientStreamStatsContracts. `now` is this tick's send decision,
      // sub-millisecond from the poll that produced `sample`.
      sampledAtEpochMs: now,
      ...sample.wire,
      ...(framePipeline ? { framePipeline } : {}),
      ...(encodedFrames ? { encodedFrames } : {}),
      ...(receiverHint ? { receiverHint } : {}),
      ...(audioReceiverHint ? { audioReceiverHint } : {}),
      ...(presentation ? { presentation } : {}),
      ...(receiverCounters ? { receiverCounters } : {}),
      // Both forms describe this wire interval; never add their duplicate counts together.
      // Engines that do not expose freezeCount must not have the raw tracker fill the gap.
      ...(freezeDelta !== undefined ? { freezeDelta } : {}),
      ...(statKeys ? { statKeys } : {}),
      ...(this.guestEngine ? { guestEngine: this.guestEngine } : {}),
    };

    // Once the calibrated path is live, hints are replaced by latencyProbe reports —
    // still keep the collector attached so reports can peek rVFC RTP timestamps.
    const hints = this.latencyProbeHandshakeLive
      ? []
      : (this.decodeHintCollector?.drain() ?? []);

    const sent = connection.send(
      JSON.stringify({
        type: CLIENT_STREAM_STATS_MESSAGE_TYPE,
        // Tentative next seq — overwritten with the assigned number below if delivered.
        seq: this.statsSeq + 1,
        ...payload,
        ...(hints.length > 0 ? { frameDecodeHints: hints } : {}),
        // The decode-lag episode rides the existing clientStats stream as an optional
        // field; older hosts ignore it. The guest only sends it when the detector
        // has an active episode (sustained backlog past the dwell).
        ...(this.lastDecodeLagEpisode?.active ? { decodeLag: this.lastDecodeLagEpisode } : {}),
      }),
    );
    // The calibrated report is its own datachannel message (the latencyProbe wire shape),
    // sent separately so the host's router never has to unwrap a clientStats payload.
    // It rides the same rate limit as clientStats: the report sends only on a tick that
    // produced a clientStats send, so the host's cadence for both signals stays aligned.
    if (this.latencyProbeHandshakeLive) {
      const report = this.latencyReportFor();
      if (report) connection.send(JSON.stringify(report));
    }

    // DEBUG BUILD ONLY — forward a copy of the clientStats payload to the HOST as a
    // `guestDebug` message, so a field replay needs only the host console (the phone's
    // console is usually unreachable). Also log locally when running with ?debug.
    // Live gate so a late `?debug` is honoured.
    if (isDebugWebrtcLoggingEnabled()) {
      // Input-to-photon rides the host-facing debug payload because that is the only place
      // a field session can be read back: the phone's console is unreachable, and this is
      // the exact record that exposed the measurement being dark in the first place.
      const photon = this.inputPhotonSummary?.();
      const debugPayload = {
        ...payload,
        hintCount: hints.length,
        // Why `encodedFrames` is or is not on the wire this session.
        encodedFrameTap: this.encodedFrameSource?.status?.() ?? 'none',
        ...(photon
          ? {
              inputPhotonLastMs: photon.lastMs,
              inputPhotonP50Ms: photon.p50Ms,
              inputPhotonP95Ms: photon.p95Ms,
              inputPhotonSamples: photon.sampleCount,
              // Which hop of the correlation went dark when sampleCount stays at 0.
              inputPhotonChain: photon.chain,
            }
          : {}),
      };
      this.sendDebugToHost(connection, 'info', debugPayload);
      console.log('[GuestStats.send]', { delivered: sent, payload: debugPayload });
    }
    // Only a delivered message advances the rate limit. Charging a failed send a full
    // interval would silently halve the host's view of a struggling link.
    if (!sent) return;
    // Same delivered-only rule: a failed or rate-limited send must not consume a sequence
    // number, or the host would read the gap as in-transit loss when nothing was sent.
    payload.seq = ++this.statsSeq;
    if (statKeys) this.receiverCounters.markStatKeysDelivered();

    // The unified stream-health record rides the same cadence as clientStats, sent only on
    // a tick whose clientStats send was accepted (and only once the peer id is known), so a
    // rate-limited or failed tick emits nothing extra. It carries the latest per-kind
    // snapshots (video/audio/input/signaling) for the host aggregator.
    if (this.peerId) {
      connection.send(JSON.stringify(this.healthRecorder.toWireMessage(this.peerId)));
    }

    this.lastStatsSentAt = now;
    this.lastStatsSent = { payload, atMs: now };
  }

  /**
   * Calibrated latency report once the handshake is live.
   *
   * Strategy chain (Phase 5): rvfc-rtp → rvfc-capture-time → estimate.
   * Never substitutes inbound-rtp.timestamp. Every emitted report carries `source`.
   */
  private latencyReportFor(): LatencyProbeReport | undefined {
    const obs = this.decodeHintCollector?.peekLatestObservation?.() ?? null;
    const hint = this.decodeHintCollector?.peekLatest();
    this.probeSeq += 1;
    const probeId = this.probeSeq;

    if (hint && Number.isFinite(hint.rtpTimestamp)) {
      return {
        type: 'latencyProbe',
        role: 'report',
        probeId,
        rtpTimestamp: hint.rtpTimestamp,
        guestDecodeWallMs: hint.decodeWallMs,
        source: 'rvfc-rtp',
        observationSequence: hint.observationSequence, observationAgeMs: hint.observationAgeMs,
        timingSource: hint.timingSource, pathEpoch: this.lastSample?.wire.recoveryEvidence?.path?.epoch,
      };
    }

    if (
      obs &&
      typeof obs.captureTime === 'number' &&
      typeof obs.receiveTime === 'number'
    ) {
      // Map capture/receive into the report fields: rtpTimestamp carries a stable key
      // derived from receiveTime (DOMHighResTimeStamp); guestDecodeWallMs is wall clock.
      return {
        type: 'latencyProbe',
        role: 'report',
        probeId,
        rtpTimestamp: Math.round(obs.receiveTime * 1000),
        guestDecodeWallMs: obs.decodeWallMs,
        source: 'rvfc-capture-time',
        observationSequence: obs.observationSequence, observationAgeMs: obs.observationAgeMs,
        timingSource: obs.timingSource, pathEpoch: this.lastSample?.wire.recoveryEvidence?.path?.epoch,
      };
    }

    // RTT-anchored estimate — only after rVFC has observed a frame (Safari path).
    // Never invent a report from inbound-rtp.timestamp alone.
    if (!obs) return undefined;

    const rttMs = this.getLastRttMs() ?? 0;
    const jitterMs = this.lastSample?.wire.jbDelayMs ?? 0;
    const processingMs =
      typeof obs.processingDuration === 'number' ? obs.processingDuration * 1000 : 0;
    const displayDeltaMs =
      typeof obs.displayNow === 'number' ? Math.max(0, obs.decodeWallMs - obs.displayNow) : 0;
    const estimateMs = rttMs / 2 + jitterMs + processingMs + displayDeltaMs;
    if (!Number.isFinite(estimateMs)) return undefined;

    return {
      type: 'latencyProbe',
      role: 'report',
      probeId,
      rtpTimestamp: Math.round(estimateMs),
      guestDecodeWallMs: obs.decodeWallMs,
      source: 'estimate',
      observationSequence: obs.observationSequence, observationAgeMs: obs.observationAgeMs,
      timingSource: obs.timingSource, pathEpoch: this.lastSample?.wire.recoveryEvidence?.path?.epoch,
    };
  }

  private probeSeq = 0;
}
